import { computed, type Ref } from 'vue'
import { contentStableComputed } from '@/utils/contentStableComputed'
import type { MeshHint } from '@/composables/useMeshHighlighting'
import type {
  ArticleTransformHint,
  StoredArticleTransform,
} from '@/composables/useArticleTransforms'
import { ArticleType, StepArticleVisibility } from '@/generated/graphql'
import type { EbomPartInstanceAllocation } from '@/utils/instanceAllocation'

export type { MeshHint }

export type StepAllocation = {
  __typename?: string
  article?: { id?: string; articleNumber: string; name?: string; type?: string }
  milliQuantity?: number
  ebomPart?: { id: string; articlePath: { articleNumbers: string[] } }
  instanceIndexes?: number[] | null
}

export type StepInputForViewer = {
  milliQuantity?: number
  output?: { article?: { articleNumber: string } }
  contentArticlePaths?: { articleNumbers: string[] }[]
}

export type StepNodeForViewer = {
  id: string
  parentId: string | null
  effectiveArticleAllocations: StepAllocation[]
  effectiveInputs: StepInputForViewer[]
  effectiveOutputs: Array<{ article: { articleNumber: string } }>
  step: {
    id: string
    viewport: {
      cameraX: number
      cameraY: number
      cameraZ: number
      targetX: number
      targetY: number
      targetZ: number
    } | null
    articleTransforms?: StoredArticleTransform[]
  }
}

/** Nodes in module order: each node followed by its substeps. */
export function flattenNodes<T extends { id: string; parentId: string | null }>(nodes: T[]): T[] {
  const result: T[] = []
  function visit(parentId: string | null) {
    nodes
      .filter((n) => n.parentId === parentId)
      .forEach((n) => {
        result.push(n)
        visit(n.id)
      })
  }
  visit(null)
  return result
}

export function ancestorsFromPath(path: string[]): string[] {
  if (path.length === 2) return [path[0]]
  return path.length > 2 ? path.slice(1, -1) : []
}

function buildAncestorHintData(a: StepAllocation): { requiredAncestors: string[] } {
  if (a.__typename !== 'EbomPartAllocation' || !a.ebomPart?.articlePath?.articleNumbers?.length) {
    return { requiredAncestors: [] }
  }
  return { requiredAncestors: ancestorsFromPath(a.ebomPart.articlePath.articleNumbers) }
}

function glbArticleNames(a: StepAllocation): string[] {
  const names = new Set<string>()
  if (a.article?.articleNumber) names.add(a.article.articleNumber)
  if (a.__typename === 'EbomPartAllocation' && a.ebomPart?.articlePath?.articleNumbers?.length) {
    const path = a.ebomPart.articlePath.articleNumbers
    names.add(path[path.length - 1])
  }
  return [...names]
}

function allocationMeshHint(a: StepAllocation): MeshHint | null {
  const names = glbArticleNames(a)
  if (names.length === 0) return null
  const { requiredAncestors } = buildAncestorHintData(a)
  return {
    articleNumber: a.article?.articleNumber ?? names[0],
    glbArticleNames: names,
    requiredAncestors,
    milliQuantity: a.milliQuantity,
    consumable: a.article?.type === ArticleType.Consumable,
    instanceIndexes: a.instanceIndexes ?? null,
  }
}

function stepAllocHints(node: StepNodeForViewer): MeshHint[] {
  return node.effectiveArticleAllocations
    .map(allocationMeshHint)
    .filter((h): h is MeshHint => h !== null)
}

function withSubsteps<T extends { id: string; parentId: string | null }>(
  ordered: T[],
  idx: number,
): T[] {
  const inScope = new Set([ordered[idx].id])
  const scope = [ordered[idx]]
  for (
    let i = idx + 1;
    i < ordered.length && ordered[i].parentId !== null && inScope.has(ordered[i].parentId!);
    i++
  ) {
    inScope.add(ordered[i].id)
    scope.push(ordered[i])
  }
  return scope
}

function stepOutputHints(node: StepNodeForViewer): MeshHint[] {
  return (node.effectiveOutputs ?? [])
    .map((o) => o.article.articleNumber)
    .filter(Boolean)
    .map((articleNumber) => ({ articleNumber, requiredAncestors: [] }))
}

function substepVisibleHints(substeps: StepNodeForViewer[]): MeshHint[] {
  return substeps.flatMap((n) => [
    ...stepAllocHints(n),
    ...stepOutputHints(n),
    ...stepInputHints(n),
  ])
}

function stepInputHints(node: StepNodeForViewer): MeshHint[] {
  return (node.effectiveInputs ?? []).flatMap((i): MeshHint[] => {
    const inputArticle = i.output?.article?.articleNumber
    if (!inputArticle) return []
    const paths = i.contentArticlePaths ?? []
    // Without resolved contents (e.g. the input article is itself an EBOM part) match it directly.
    if (paths.length === 0) {
      return [
        {
          articleNumber: inputArticle,
          requiredAncestors: [],
          milliQuantity: i.milliQuantity,
          mismatchExempt: true,
        },
      ]
    }
    // Every contained part carries the input's article number, so the whole input renders in one colour
    // (colour keys off articleNumber) while each part still matches its own mesh via glbArticleNames.
    return paths.map((p) => {
      const numbers = p.articleNumbers
      return {
        articleNumber: inputArticle,
        glbArticleNames: [numbers[numbers.length - 1] ?? inputArticle],
        requiredAncestors: ancestorsFromPath(numbers),
        milliQuantity: i.milliQuantity,
        mismatchExempt: true,
      }
    })
  })
}

function allocationTransformHint(a: StepAllocation): ArticleTransformHint | null {
  const names = glbArticleNames(a)
  const articleId = a.article?.id
  if (names.length === 0 || !articleId) return null
  const { requiredAncestors } = buildAncestorHintData(a)
  return {
    articleId,
    articleNumber: a.article?.articleNumber ?? names[0],
    glbArticleNames: names,
    requiredAncestors,
    milliQuantity: a.milliQuantity,
  }
}

function stepTransformHints(node: StepNodeForViewer): ArticleTransformHint[] {
  return node.effectiveArticleAllocations
    .map(allocationTransformHint)
    .filter((h): h is ArticleTransformHint => h !== null)
}

export function useStepMeshHints(
  currentNodeId: Ref<string | undefined>,
  nodes: Ref<StepNodeForViewer[]>,
  visibilityMode: Ref<StepArticleVisibility>,
) {
  const orderedStepNodes = computed(() => flattenNodes(nodes.value))

  const visibleMeshHints = contentStableComputed<MeshHint[] | null>(() => {
    const ordered = orderedStepNodes.value
    const idx = ordered.findIndex((n) => n.id === currentNodeId.value)
    if (idx < 0) return null

    const currentNode = ordered[idx]
    const substeps = withSubsteps(ordered, idx).slice(1)
    const mode = visibilityMode.value

    if (mode === StepArticleVisibility.ShowAll) return null

    if (mode === StepArticleVisibility.HidePrevious) {
      return [currentNode, ...substeps].flatMap((n) => stepAllocHints(n))
    }

    // SHOW_SUBMODULE limits the background to earlier steps within the same group of
    // substeps (steps sharing the current step's immediate parent).
    const previous = ordered.slice(0, idx)
    const inScope =
      mode === StepArticleVisibility.ShowSubmodule
        ? previous.filter((n) => n.parentId === currentNode.parentId)
        : previous

    const background = inScope.flatMap((n) => [
      ...(stepOutputHints(n).length > 0 ? stepOutputHints(n) : stepAllocHints(n)),
      ...stepInputHints(n),
    ])

    const hints = [
      ...background,
      ...stepAllocHints(currentNode),
      ...stepOutputHints(currentNode),
      ...stepInputHints(currentNode),
      ...substepVisibleHints(substeps),
    ]
    // With nothing to filter on keep the whole model visible so parts
    // can still be picked. HIDE_PREVIOUS deliberately does not get this fallback.
    return hints.length > 0 ? hints : null
  })

  const currentStepHighlightHints = contentStableComputed<MeshHint[]>(() => {
    const ordered = orderedStepNodes.value
    const idx = ordered.findIndex((n) => n.id === currentNodeId.value)
    if (idx < 0) return []
    const scope = withSubsteps(ordered, idx)

    const allocs = scope
      .flatMap((n) => n.effectiveArticleAllocations)
      .sort((a, b) => {
        const typeCmp = (a.__typename ?? '').localeCompare(b.__typename ?? '')
        if (typeCmp !== 0) return typeCmp
        const artTypeCmp = (a.article?.type ?? '').localeCompare(b.article?.type ?? '')
        if (artTypeCmp !== 0) return artTypeCmp
        return (a.article?.name ?? '').localeCompare(b.article?.name ?? '')
      })

    const allocationHints = allocs.map(allocationMeshHint).filter((h): h is MeshHint => h !== null)

    return [...allocationHints, ...scope.flatMap((n) => stepInputHints(n))]
  })

  const editableTransformHints = contentStableComputed<ArticleTransformHint[]>(() => {
    const ordered = orderedStepNodes.value
    const idx = ordered.findIndex((n) => n.id === currentNodeId.value)
    if (idx < 0) return []
    return ordered[idx].effectiveArticleAllocations.flatMap((a) => {
      const hint = allocationTransformHint(a)
      return hint ? [{ ...hint, instanceIndexes: a.instanceIndexes ?? null }] : []
    })
  })

  const currentStepEbomPartAllocations = contentStableComputed<EbomPartInstanceAllocation[]>(() => {
    const node = orderedStepNodes.value.find((n) => n.id === currentNodeId.value)
    return (node?.effectiveArticleAllocations ?? []).flatMap((a) =>
      a.ebomPart
        ? [
            {
              ebomPartId: a.ebomPart.id,
              milliQuantity: a.milliQuantity ?? 0,
              instanceIndexes: a.instanceIndexes ?? null,
            },
          ]
        : [],
    )
  })

  const moduleTransformHints = contentStableComputed<ArticleTransformHint[]>(() => {
    const byArticleAndPath = new Map<string, ArticleTransformHint>()
    for (const node of orderedStepNodes.value) {
      for (const hint of stepTransformHints(node)) {
        const key = `${hint.articleId}|${hint.requiredAncestors.join('>')}`
        if (!byArticleAndPath.has(key)) byArticleAndPath.set(key, hint)
      }
    }
    return [...byArticleAndPath.values()]
  })

  const currentStepArticleTransforms = contentStableComputed<StoredArticleTransform[]>(() => {
    const ordered = orderedStepNodes.value
    return ordered.find((n) => n.id === currentNodeId.value)?.step.articleTransforms ?? []
  })

  const savedViewport = contentStableComputed(() => {
    const ordered = orderedStepNodes.value
    return ordered.find((n) => n.id === currentNodeId.value)?.step.viewport ?? null
  })

  return {
    orderedStepNodes,
    visibleMeshHints,
    currentStepHighlightHints,
    moduleTransformHints,
    currentStepArticleTransforms,
    editableTransformHints,
    currentStepEbomPartAllocations,
    savedViewport,
  }
}
